import {objectFromEntries} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React, {useEffect, useState} from 'react';

import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {trackOpenedRelevantTemplatesTab} from 'nav/analytics/RelevantTemplatesAnalytics';
import {Organization as OrganizationType} from 'orgs/OrganizationConstants';
import {getStore} from 'redux-stores/Store';
import {BaseFieldTableProps, ClientSchemaField} from 'registry/field/FieldTable';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssaySchemaPermissionSettings from 'results/AssaySchemaPermissionSettings';
import AssayStore from 'results/AssayStore';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import {RELEVANT_TEMPLATES_TAB_NAME} from 'schemas/relevant-templates/RelevantTemplatesConstants';
import RelevantTemplatesTab from 'schemas/relevant-templates/RelevantTemplatesTab';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import EntityStore from 'stores/EntityStore';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {capitalize} from 'util/NameUtil';

import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';
import {AssaySchemaEditorQuery, AssaySchemaEditorQueryVariables} from './__generated/AssaySchemaEditorQuery';
import {AssaySchema, getJsonUrl, SCHEMA_TYPE_NAME} from './AssaySchemaConstants';
import AssaySchemaCreateFlow from './AssaySchemaCreateFlow';
import * as AssaySchemaDAO from './AssaySchemaDAO';
import AssaySchemaMetadataSettings from './AssaySchemaMetadataSettings';
import AssaySchemaToolbar from './AssaySchemaToolbar';
import setWindowTitleForSchema from './setWindowTitleForSchema';

type RunSchemaOrInaccessible = NonNullable<AssaySchemaEditorQuery['runSchema']>;
type RunSchema = ExtractByTypename<RunSchemaOrInaccessible, 'RunSchemaNode'>;
type ResultSchemaOrInaccessible = NonNullable<AssaySchemaEditorQuery['resultSchema']>;
type ResultSchema = ExtractByTypename<ResultSchemaOrInaccessible, 'ResultSchemaNode'>;
type Organization = NonNullable<AssaySchemaEditorQuery['currentTenantOrganizations']>[number];

export interface AssaySchemaEditorProps {
  schemaId?: string;
  schemaType: LegacyAssaySchemaType.ASSAY_RESULT | LegacyAssaySchemaType.ASSAY_RUN;
  autosave: boolean;
  baseTestAttrs: {component: string};
  publishDefaultDataTableConfig: boolean;
}

export enum Tabs {
  ASSAY_SCHEMA_GUI_EDITOR = 'ASSAY_SCHEMA_GUI_EDITOR',
  ASSAY_SCHEMA_ACCESS_POLICIES = 'ACCESS ASSAY_SCHEMA_ACCESS_POLICIES',
  ASSAY_SCHEMA_RELEVANT_TEMPLATES = 'ASSAY_SCHEMA_RELEVANT_TEMPLATES',
}

export interface AssaySchemaEditorQueriedProps {
  schemaHasData: boolean; // Have any items of this schema been created?
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  schemaPermissions: SchemaPermissionsGraphQL | null;
  organizations: Array<Organization>;
}

export interface AssaySchemaEditorConnectedProps {
  errors: Array<string> | null;
  errorMessage: string | null;
  ownerChoices: Array<Owner> | null;
  ownerId: string;
  name: string;
  sqlIdentifier: string | null;
  schema: AssaySchema | null;
  inputFileConfigs: Array<AutomationInputFileConfigSpec>;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>;
  fields: Array<ClientSchemaField | SchemaField> | null;
  hasUnsavedData: boolean;
  hasUnsavedInputFileConfigData: boolean;
  hasUnsavedOutputFileConfigData: boolean;
  allowCreateRunFromNotebook: boolean;
  allowInsertRunFromInbox: boolean;
  shouldRecordEquipment: boolean;
  connectionSchemaId: string | null;
  schemaPermissions: SchemaPermissionsGraphQL | null;
  sessionId: string;
}

export class AssaySchemaEditorConnector extends DataConnector<
  AssaySchemaEditorProps & AssaySchemaEditorQueriedProps,
  AssaySchemaEditorConnectedProps
> {
  enhanceProps(): AssaySchemaEditorConnectedProps {
    const schema = this.props.schemaId ? AssayStore.getAssaySchema(this.props.schemaId) : null;
    const schemaPermissions = this.props.schemaPermissions;
    const schemaConfig = this.reduxState().ui.schemaConfig;
    const {
      sessionId,
      errorState,
      ownerChoices,
      inputFileConfigs,
      outputFileConfigs,
      hasUnsavedData,
      hasUnsavedInputFileConfigData,
      hasUnsavedOutputFileConfigData,
      allowCreateRunFromNotebook,
      allowInsertRunFromInbox,
      shouldRecordEquipment,
      connectionSchemaId,
    } = schemaConfig;
    let fields: Array<ClientSchemaField | SchemaField> | null = schemaConfig.fields;
    let {name, ownerId, sqlIdentifier} = schemaConfig;
    const {errors, errorMessage} = errorState != null ? errorState : {errors: null, errorMessage: null};
    if (schema) {
      ownerId = schema.organization.id;
      name = name == null ? schema.name : name;
      sqlIdentifier = sqlIdentifier == null ? schema.sqlIdentifier : sqlIdentifier;
    } else if (fields == null) {
      fields = [];
    }

    // TODO(BNCH-16136) remove this once the FieldTable can handle non-registry schema naming conventions
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const processFieldsForDisplay = (_fields: Array<ClientSchemaField | SchemaField>) =>
      _fields.map((field) => ({...field, name: field.displayName}));

    if (fields != null) {
      fields = processFieldsForDisplay(fields);
    }

    return {
      errors,
      errorMessage,
      ownerChoices,
      ownerId: ownerId!,
      name: name == null ? '' : name,
      sqlIdentifier,
      schema,
      inputFileConfigs: inputFileConfigs == null ? [] : inputFileConfigs,
      outputFileConfigs: outputFileConfigs == null ? [] : outputFileConfigs,
      fields,
      hasUnsavedData,
      hasUnsavedInputFileConfigData,
      hasUnsavedOutputFileConfigData,
      allowCreateRunFromNotebook,
      allowInsertRunFromInbox,
      shouldRecordEquipment,
      connectionSchemaId,
      schemaPermissions,
      sessionId,
    };
  }
}

export function AssaySchemaEditor(
  props: AssaySchemaEditorProps & AssaySchemaEditorQueriedProps & AssaySchemaEditorConnectedProps
): JSX.Element {
  const [isDataStale, setIsDataStale] = useState(true);
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.ASSAY_SCHEMA_GUI_EDITOR);

  const unsavedDataMessage = `Your ${
    SCHEMA_TYPE_NAME[props.schemaType]
  } schema has unsaved changes. Do you want to leave the page and discard your changes?`;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const beforeUnloadHandler = () => {
    if (!props.hasUnsavedData) {
      return;
    }
    return unsavedDataMessage;
  };

  useEffect(() => {
    $(window).on('beforeunload', beforeUnloadHandler);

    // this will clean up the event every time the component is re-rendered
    return function cleanup(): void {
      $(window).off('beforeunload', beforeUnloadHandler);
    };
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleNavigateToSchemaListing = () => {
    BenchlingUtil.navigate(null, AssaySchemaDAO.assaySchemasURL(props.schemaType));
  };

  const {schemaId} = props;

  useEffect(() => {
    setIsDataStale(true);
    getStore().dispatch(SchemaConfigReduxActions.resetSchemaSpecUi());
    if (schemaId) {
      AssaySchemaActions.fetchSchemaSpec(schemaId, false);
    }
    setIsDataStale(false);
  }, [schemaId]);

  const isLoading =
    isDataStale || props.ownerChoices == null || props.fields == null || (props.schemaId && !props.schema);

  if (isLoading) {
    return <CenteredHorizontalThrobber key="throbber" />;
  } else if (!props.schemaId && props.ownerChoices!.length === 0) {
    return <NoOwnersSchemaMessage />;
  }

  const containerProps = {
    title: `${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Schemas`,
    subtitle: props.schemaId
      ? props.schema!.name
      : `Create ${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Schema`,
    onClickTitle: handleNavigateToSchemaListing,
    toolbar: props.schema && (
      <AssaySchemaToolbar schemaId={props.schema.id} schemaType={props.schemaType} isSpecJsonFormat={false} />
    ),
  };

  if (props.schema?.derivedParent != null) {
    BenchlingUtil.navigate(null, getJsonUrl(props.schemaType, props.schemaId));
  }

  function trackOpenTabEvent(tab: Tabs): void {
    if (tab === Tabs.ASSAY_SCHEMA_RELEVANT_TEMPLATES && props.schemaId) {
      trackOpenedRelevantTemplatesTab({
        schemaType: props.schemaType,
        schemaId: props.schemaId,
        isAdmin: EntityStore.currentUser().organizations.some((org: OrganizationType) => org.admin),
      });
    }
  }

  function handleSetActiveTab(tab: Tabs): void {
    setActiveTab(tab);
    trackOpenTabEvent(tab);
  }

  function shouldRenderPermissionSettingsTab(): boolean {
    return BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS && props.schema != null;
  }

  function shouldRenderRelevantTemplatesTab(): boolean {
    return (
      BenchlingConfig.SCHEMAS_UI_ENABLE_RELEVANT_TEMPLATES_TAB &&
      props.schemaId != null &&
      props.schemaType === LegacyAssaySchemaType.ASSAY_RESULT
    );
  }

  setWindowTitleForSchema(props.schema, props.schemaType);

  return (
    <SettingsPageContainer {...containerProps}>
      {schemaId ? (
        <TabContainer
          activeTab={activeTab}
          onRequestTabChange={handleSetActiveTab}
          className="u-flex-grow u-min-width-zero"
        >
          <Tab
            tabKey={Tabs.ASSAY_SCHEMA_GUI_EDITOR}
            title={`${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Settings`}
          >
            <AssaySchemaMetadataSettings
              {...props}
              shouldRenderPermissionSettingsTab={shouldRenderPermissionSettingsTab()}
              unsavedDataMessage={unsavedDataMessage}
            />
          </Tab>
          {shouldRenderPermissionSettingsTab() && (
            <Tab tabKey={Tabs.ASSAY_SCHEMA_ACCESS_POLICIES} title="Access Policies">
              <AssaySchemaPermissionSettings assaySchemaId={schemaId} />
            </Tab>
          )}
          {props.schema != null && shouldRenderRelevantTemplatesTab() && (
            <Tab tabKey={Tabs.ASSAY_SCHEMA_RELEVANT_TEMPLATES} title={RELEVANT_TEMPLATES_TAB_NAME}>
              <RelevantTemplatesTab
                schemaId={props.schema.id}
                schemaOrgUrlId={props.schema.organization.url_identifier}
              />
            </Tab>
          )}
        </TabContainer>
      ) : (
        <AssaySchemaCreateFlow
          {...props}
          shouldRenderPermissionSettingsTab={shouldRenderPermissionSettingsTab()}
          unsavedDataMessage={unsavedDataMessage}
          organizations={props.organizations}
        />
      )}
    </SettingsPageContainer>
  );
}

const ConnectedAssaySchemaEditor = AssaySchemaEditorConnector.connect(AssaySchemaEditor);

export const ASSAY_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query AssaySchemaEditorQuery($id: StrictString) @skipAudit @engComponent(name: RESULTS) {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
    resultSchema(id: $id) {
      ... on ResultSchemaNode {
        id
        hasData
        allFields {
          id
          apiId
          dependentsSummaries
        }
        ... on PoliceableInterface {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
        }
      }
    }
    runSchema(id: $id) {
      ... on RunSchemaNode {
        id
        hasData
        allFields {
          id
          apiId
          dependentsSummaries
        }
        ... on PoliceableInterface {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
        }
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
  ${getFragmentWrapper('SchemaPermissions').fragment}
`;

function isResultSchema(s?: ResultSchemaOrInaccessible | null): s is ResultSchema {
  return s?.__typename === 'ResultSchemaNode';
}

function isRunSchema(s?: RunSchemaOrInaccessible | null): s is RunSchema {
  return s?.__typename === 'RunSchemaNode';
}

function QueryingAssaySchemaEditor(props: AssaySchemaEditorProps): JSX.Element {
  return (
    <BenchlingQuery<AssaySchemaEditorQuery, AssaySchemaEditorQueryVariables>
      query={ASSAY_SCHEMA_EDITOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
      variables={{id: props.schemaId}}
    >
      {(data) => {
        const schemaHasData =
          (isResultSchema(data.resultSchema) && data.resultSchema.hasData) ||
          (isRunSchema(data.runSchema) && data.runSchema.hasData);
        const schemaFieldDependentsSummariesById = objectFromEntries(
          (
            (isResultSchema(data.resultSchema) && data.resultSchema.allFields) ||
            (isRunSchema(data.runSchema) && data.runSchema.allFields) ||
            []
          ).map((field) => [field.apiId, field.dependentsSummaries])
        );

        const schemaPermissions =
          (isResultSchema(data.resultSchema) && data.resultSchema.permissions) ||
          (isRunSchema(data.runSchema) && data.runSchema.permissions);

        const organizations = data.currentTenantOrganizations;

        return (
          <ConnectedAssaySchemaEditor
            {...props}
            schemaHasData={schemaHasData}
            schemaFieldDependentsSummariesById={schemaFieldDependentsSummariesById}
            schemaPermissions={schemaPermissions as SchemaPermissionsGraphQL}
            organizations={organizations}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export const assaySchemaEditorStyles = StyleSheet.create({
  fieldSection: {
    marginBottom: '32px',
  },
});

export default QueryingAssaySchemaEditor;
